/**
 * Spec 030: the sections of `/admin/brand-settings`, in page order. One list
 * drives the card anchors, the left icon rail and the phone drawer, so the
 * navigation can never point at a card that is not on the page.
 */
import type { NavGroup, NavSection } from '../../components/SectionNav/types';

export type BrandSettingsGroup = 'launch' | 'reach' | 'tripRules' | 'hosts';

export const BRAND_SETTINGS_GROUPS: NavGroup<BrandSettingsGroup>[] = [
  { id: 'launch', label: 'Launch' },
  { id: 'reach', label: 'Reach' },
  { id: 'tripRules', label: 'Trip rules' },
  { id: 'hosts', label: 'Hosts' },
];

export interface BrandSettingsSection extends NavSection<BrandSettingsGroup> {
  /** Shown only on a brand that sells cleaning and handover jobs. */
  servicesOnly?: boolean;
}

export const BRAND_SETTINGS_SECTIONS: BrandSettingsSection[] = [
  { id: 'bookings', label: 'Bookings', short: 'Bookings', icon: 'calendarCheck', group: 'launch' },
  { id: 'region', label: 'Region', short: 'Region', icon: 'globe', group: 'launch' },
  {
    id: 'other-brands',
    label: 'Other brands in search',
    short: 'Search',
    icon: 'search',
    group: 'reach',
  },
  {
    id: 'appear-in',
    label: 'Where this brand may appear',
    short: 'Areas',
    icon: 'mapPin',
    group: 'reach',
  },
  {
    id: 'exclusive-markets',
    label: 'Exclusive markets',
    short: 'Markets',
    icon: 'flag',
    group: 'reach',
  },
  {
    id: 'referral',
    label: 'Referral fee and emails',
    short: 'Referral',
    icon: 'percent',
    group: 'reach',
  },
  {
    id: 'cancellation',
    label: 'Cancellation rules',
    short: 'Cancel',
    icon: 'calendarX',
    group: 'tripRules',
  },
  {
    id: 'service-rules',
    label: 'Service rules',
    short: 'Services',
    icon: 'sparkles',
    group: 'tripRules',
    servicesOnly: true,
  },
  { id: 'car-settings', label: 'Car settings', short: 'Cars', icon: 'car', group: 'tripRules' },
  {
    id: 'booking-rules',
    label: 'Booking rules',
    short: 'Rules',
    icon: 'clipboard',
    group: 'tripRules',
  },
  {
    id: 'host-permissions',
    label: 'Host permissions',
    short: 'Hosts',
    icon: 'users',
    group: 'hosts',
  },
  { id: 'history', label: 'Change history', short: 'History', icon: 'history', group: 'hosts' },
];

/** The sections this brand's page shows, in page order. */
export function visibleBrandSettingsSections(ctx: {
  sellsServices: boolean;
}): BrandSettingsSection[] {
  return BRAND_SETTINGS_SECTIONS.filter(s => !s.servicesOnly || ctx.sellsServices);
}

/** Anchor ids, so the cards and the nav share one spelling. */
export const BRAND_SECTION_ID = {
  bookings: 'bookings',
  region: 'region',
  otherBrands: 'other-brands',
  appearIn: 'appear-in',
  exclusiveMarkets: 'exclusive-markets',
  referral: 'referral',
  cancellation: 'cancellation',
  serviceRules: 'service-rules',
  carSettings: 'car-settings',
  bookingRules: 'booking-rules',
  hostPermissions: 'host-permissions',
  history: 'history',
} as const;
